import { createI18n } from 'vue-i18n'

import en from './locales/en'
import hr from './locales/hr'
import sl from './locales/sl'

export type AvailableLocales = 'en' | 'sl' | 'hr'

/*
 * How many of a thing there are, in languages that do not count the way English does.
 *
 * Vue I18n picks the form by index and its built-in rule knows one and other, which is
 * English and nothing else. Slovenian has four forms (1 napaka, 2 napaki, 3 napake,
 * 5 napak) and Croatian three, and a console that said "2 napak" to the person whose
 * language it is would be a console written by somebody who did not speak it.
 *
 * The rules are CLDR's. The index is clamped to what the message actually offers, so a
 * string written with fewer forms than the language has still renders rather than
 * falling off the end.
 */
const pick = (index: number, choices: number): number => Math.min(index, choices - 1)

const slovenian = (choice: number, choices: number): number => {
    const hundreds = Math.abs(choice) % 100

    if (hundreds === 1)                  return pick(0, choices)
    if (hundreds === 2)                  return pick(1, choices)
    if (hundreds === 3 || hundreds === 4) return pick(2, choices)

    return pick(3, choices)
}

const croatian = (choice: number, choices: number): number => {
    const tens     = Math.abs(choice) % 10
    const hundreds = Math.abs(choice) % 100

    if (tens === 1 && hundreds !== 11)                          return pick(0, choices)
    if (tens >= 2 && tens <= 4 && (hundreds < 12 || hundreds > 14)) return pick(1, choices)

    return pick(2, choices)
}

/*
 * English is the default and the fallback: the console is read by people in more than
 * one country, and a key that exists in only one file still renders rather than
 * showing its own name. Slovenian and Croatian are complete translations; the choice
 * is remembered per browser under `locale`.
 */
const i18n = createI18n({
    legacy:           false,
    globalInjection:  true,
    locale:           localStorage.getItem('locale') || 'en',
    fallbackLocale:   'en',
    messages:         { en, sl, hr },
    availableLocales: ['en', 'sl', 'hr'],
    pluralRules:      { sl: slovenian, hr: croatian },
})

export default i18n
